import React, { useState, useEffect } from 'react';
import { firebaseLeadsService, Lead } from '../../services/FirebaseLeadsService';
import { commpeakService } from '../../services/CommpeakService';
import CallHistoryModal from '../../components/CallHistoryModal';
import './LeadsManager.css';

const LeadsManager: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sourceFilter, setSourceFilter] = useState<string>('All');
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    qualified: 0,
    converted: 0,
    conversionRate: 0,
    totalValue: 0,
    avgDealValue: 0
  });

  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    tradingExperience: 'Beginner' as Lead['tradingExperience'],
    interestedBroker: '',
    interestedPlatform: '',
    leadSource: 'Website' as Lead['leadSource'],
    status: 'New' as Lead['status'],
    priority: 'Medium' as Lead['priority'],
    notes: '',
    campaignId: '',
    adSpend: 0,
    conversionValue: 0,
    assignedTo: 'Admin',
    tags: [] as string[]
  });

  useEffect(() => {
    loadLeads();
    loadStats();
  }, []);

  const filterLeads = React.useCallback(() => {
    let filtered = leads;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm) ||
        lead.country.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    // Filter by source
    if (sourceFilter !== 'All') {
      filtered = filtered.filter(lead => lead.leadSource === sourceFilter);
    }

    setFilteredLeads(filtered);
  }, [leads, searchTerm, statusFilter, sourceFilter]);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, statusFilter, sourceFilter, filterLeads]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const allLeads = await firebaseLeadsService.getAllLeads();
      setLeads(allLeads);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const leadsStats = await firebaseLeadsService.getLeadsStats();
      setStats(leadsStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingLead) {
        await firebaseLeadsService.updateLead(editingLead.id, leadForm);
      } else {
        await firebaseLeadsService.addLead(leadForm);
      }

      await loadLeads();
      await loadStats();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Failed to save lead');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setLeadForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      country: '',
      tradingExperience: 'Beginner',
      interestedBroker: '',
      interestedPlatform: '',
      leadSource: 'Website',
      status: 'New',
      priority: 'Medium',
      notes: '',
      campaignId: '',
      adSpend: 0,
      conversionValue: 0,
      assignedTo: 'Admin',
      tags: []
    });
    setEditingLead(null);
  };

  const openModal = (lead?: Lead) => {
    if (lead) {
      setLeadForm({
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email,
        phone: lead.phone,
        country: lead.country,
        tradingExperience: lead.tradingExperience,
        interestedBroker: lead.interestedBroker,
        interestedPlatform: lead.interestedPlatform,
        leadSource: lead.leadSource,
        status: lead.status,
        priority: lead.priority,
        notes: lead.notes,
        campaignId: lead.campaignId || '',
        adSpend: lead.adSpend || 0,
        conversionValue: lead.conversionValue || 0,
        assignedTo: lead.assignedTo,
        tags: lead.tags
      });
      setEditingLead(lead);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const deleteLead = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      setLoading(true);
      try {
        await firebaseLeadsService.deleteLead(id);
        await loadLeads();
        await loadStats();
      } catch (error) {
        console.error('Error deleting lead:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const updateLeadStatus = async (id: string, status: Lead['status']) => {
    setLoading(true);
    try {
      await firebaseLeadsService.updateLeadStatus(id, status);
      await loadLeads();
      await loadStats();
    } catch (error) {
      console.error('Error updating lead status:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };

  const openCallModal = (lead: Lead) => {
    setSelectedLead(lead);
    setShowCallModal(true);
  };

  const handleQuickCall = async (lead: Lead) => {
    if (!commpeakService.isValidPhoneNumber(lead.phone)) {
      alert('Invalid phone number format');
      return;
    }

    const confirmed = window.confirm(
      `Initiate call to ${lead.firstName} ${lead.lastName} at ${lead.phone}?`
    );

    if (confirmed) {
      try {
        const response = await commpeakService.initiateCall(
          '+1234567890', // Your default caller number
          commpeakService.formatPhoneNumber(lead.phone),
          lead.id
        );

        if (response.success) {
          alert('Call initiated successfully! Check your phone.');
          // Update lead status to contacted if it's new
          if (lead.status === 'New') {
            await updateLeadStatus(lead.id, 'Contacted');
          }
        } else {
          alert(`Failed to initiate call: ${response.message}`);
        }
      } catch (error) {
        console.error('Quick call error:', error);
        alert('Failed to initiate call. Please try again.');
      }
    }
  };

  return (
    <div className="leads-management">
      <div className="leads-header">
        <h2 className="leads-title">📊 Leads Manager</h2>
        <div className="leads-controls">
          <div className="leads-search">
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <select 
            className="filter-dropdown"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="New">New</option>
            <option value="Contacted">Contacted</option>
            <option value="Qualified">Qualified</option>
            <option value="Demo Account">Demo Account</option>
            <option value="Live Account">Live Account</option>
            <option value="Converted">Converted</option>
            <option value="Not Interested">Not Interested</option>
          </select>

          <select 
            className="filter-dropdown"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="All">All Sources</option>
            <option value="Website">Website</option>
            <option value="Facebook">Facebook</option>
            <option value="Google Ads">Google Ads</option>
            <option value="Instagram">Instagram</option>
            <option value="YouTube">YouTube</option>
            <option value="Referral">Referral</option>
            <option value="Other">Other</option>
          </select>

          <button className="add-lead-btn" onClick={() => openModal()}>
            ➕ Add Lead
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="leads-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Leads</div>
        </div>
        
        <div className="stat-card new-leads">
          <div className="stat-number">{stats.new}</div>
          <div className="stat-label">New Leads</div>
        </div>
        
        <div className="stat-card contacted">
          <div className="stat-number">{stats.contacted}</div>
          <div className="stat-label">Contacted</div>
        </div>
        
        <div className="stat-card qualified">
          <div className="stat-number">{stats.qualified}</div>
          <div className="stat-label">Qualified</div>
        </div>
        
        <div className="stat-card converted">
          <div className="stat-number">{stats.converted}</div>
          <div className="stat-label">Converted</div>
          <div className="stat-percentage">{stats.conversionRate.toFixed(1)}% Rate</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-number">{formatCurrency(stats.totalValue)}</div>
          <div className="stat-label">Total Value</div>
          <div className="stat-percentage">Avg: {formatCurrency(stats.avgDealValue)}</div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="leads-table-container">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Contact</th>
              <th>Experience</th>
              <th>Interest</th>
              <th>Source</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Value</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((lead) => (
              <tr key={lead.id}>
                <td>
                  <div className="contact-info">
                    <strong>{lead.firstName} {lead.lastName}</strong>
                    <span>{lead.email}</span>
                    <span>{lead.phone}</span>
                    <span>{lead.country}</span>
                  </div>
                </td>
                <td>
                  <span className="lead-source">{lead.tradingExperience}</span>
                </td>
                <td>
                  <div>
                    <div>{lead.interestedBroker}</div>
                    <small style={{ color: 'rgba(255,255,255,0.6)' }}>
                      {lead.interestedPlatform}
                    </small>
                  </div>
                </td>
                <td>
                  <span className="lead-source">{lead.leadSource}</span>
                </td>
                <td>
                  <select
                    className={`lead-status ${lead.status.toLowerCase().replace(' ', '-')}`}
                    value={lead.status}
                    onChange={(e) => updateLeadStatus(lead.id, e.target.value as Lead['status'])}
                    style={{ 
                      background: 'transparent', 
                      border: 'none', 
                      color: 'inherit',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="New">New</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Qualified">Qualified</option>
                    <option value="Demo Account">Demo Account</option>
                    <option value="Live Account">Live Account</option>
                    <option value="Converted">Converted</option>
                    <option value="Not Interested">Not Interested</option>
                  </select>
                </td>
                <td>
                  <span className={`lead-priority ${lead.priority.toLowerCase()}`}>
                    {lead.priority}
                  </span>
                </td>
                <td>
                  {lead.conversionValue ? formatCurrency(lead.conversionValue) : '-'}
                </td>
                <td>{formatDate(lead.createdAt)}</td>
                <td>
                  <div className="lead-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => openModal(lead)}
                      title="Edit Lead"
                    >
                      ✏️
                    </button>
                    <button 
                      className="action-btn call"
                      onClick={() => handleQuickCall(lead)}
                      title="Quick Call"
                    >
                      📞
                    </button>
                    <button 
                      className="action-btn call-history"
                      onClick={() => openCallModal(lead)}
                      title="Call Center & History"
                    >
                      📋
                    </button>
                    <button 
                      className="action-btn email"
                      onClick={() => window.open(`mailto:${lead.email}`)}
                      title="Email Lead"
                    >
                      ✉️
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => deleteLead(lead.id)}
                      title="Delete Lead"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredLeads.length === 0 && !loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: 'rgba(255,255,255,0.6)' 
          }}>
            {searchTerm || statusFilter !== 'All' || sourceFilter !== 'All' 
              ? 'No leads match your filters' 
              : 'No leads found. Add your first lead!'}
          </div>
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      {showModal && (
        <div className="lead-modal">
          <div className="lead-modal-content">
            <h3>{editingLead ? '✏️ Edit Lead' : '➕ Add New Lead'}</h3>
            
            <form onSubmit={handleSubmit} className="lead-form">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={leadForm.firstName}
                  onChange={(e) => setLeadForm({...leadForm, firstName: e.target.value})}
                  required
                  placeholder="John"
                />
              </div>

              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={leadForm.lastName}
                  onChange={(e) => setLeadForm({...leadForm, lastName: e.target.value})}
                  required
                  placeholder="Doe"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                  required
                  placeholder="john@example.com"
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm({...leadForm, phone: e.target.value})}
                  required
                  placeholder="+1234567890"
                />
              </div>

              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={leadForm.country}
                  onChange={(e) => setLeadForm({...leadForm, country: e.target.value})}
                  placeholder="United States"
                />
              </div>

              <div className="form-group">
                <label>Trading Experience</label>
                <select
                  value={leadForm.tradingExperience}
                  onChange={(e) => setLeadForm({...leadForm, tradingExperience: e.target.value as Lead['tradingExperience']})}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div className="form-group">
                <label>Interested Broker</label>
                <input
                  type="text"
                  value={leadForm.interestedBroker}
                  onChange={(e) => setLeadForm({...leadForm, interestedBroker: e.target.value})}
                  placeholder="Exness, XM, etc."
                />
              </div>

              <div className="form-group">
                <label>Interested Platform</label>
                <input
                  type="text"
                  value={leadForm.interestedPlatform}
                  onChange={(e) => setLeadForm({...leadForm, interestedPlatform: e.target.value})}
                  placeholder="MT4, MT5, cTrader"
                />
              </div>

              <div className="form-group">
                <label>Lead Source</label>
                <select
                  value={leadForm.leadSource}
                  onChange={(e) => setLeadForm({...leadForm, leadSource: e.target.value as Lead['leadSource']})}
                >
                  <option value="Website">Website</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Google Ads">Google Ads</option>
                  <option value="Instagram">Instagram</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Referral">Referral</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={leadForm.status}
                  onChange={(e) => setLeadForm({...leadForm, status: e.target.value as Lead['status']})}
                >
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Qualified">Qualified</option>
                  <option value="Demo Account">Demo Account</option>
                  <option value="Live Account">Live Account</option>
                  <option value="Converted">Converted</option>
                  <option value="Not Interested">Not Interested</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  value={leadForm.priority}
                  onChange={(e) => setLeadForm({...leadForm, priority: e.target.value as Lead['priority']})}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Campaign ID</label>
                <input
                  type="text"
                  value={leadForm.campaignId}
                  onChange={(e) => setLeadForm({...leadForm, campaignId: e.target.value})}
                  placeholder="FB_001, GGL_002"
                />
              </div>

              <div className="form-group">
                <label>Ad Spend ($)</label>
                <input
                  type="number"
                  value={leadForm.adSpend}
                  onChange={(e) => setLeadForm({...leadForm, adSpend: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Conversion Value ($)</label>
                <input
                  type="number"
                  value={leadForm.conversionValue}
                  onChange={(e) => setLeadForm({...leadForm, conversionValue: parseFloat(e.target.value) || 0})}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={leadForm.notes}
                  onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                  placeholder="Additional notes about this lead..."
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="modal-btn cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="modal-btn save"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingLead ? 'Update Lead' : 'Add Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Call History Modal */}
      {showCallModal && selectedLead && (
        <CallHistoryModal
          leadId={selectedLead.id}
          leadName={`${selectedLead.firstName} ${selectedLead.lastName}`}
          leadPhone={selectedLead.phone}
          isOpen={showCallModal}
          onClose={() => {
            setShowCallModal(false);
            setSelectedLead(null);
          }}
        />
      )}
    </div>
  );
};

export default LeadsManager;